/* =============================================
   DiagnosticServices.jsx — TANBIO
   Card grid. "View Tests" navigates to /tests/:slug
   ============================================= */
import { useEffect, useRef, forwardRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SERVICES from "../data/services.data";
import "../styles/DiagnosticServices.css";

export default function DiagnosticServices() {
  const navigate   = useNavigate();
  const sectionRef = useRef(null);
  const cardRefs   = useRef([]);

  useEffect(() => {
    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("ds-card--visible");
            cardObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    cardRefs.current.forEach((el) => { if (el) cardObserver.observe(el); });

    const headObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("ds-header--visible");
            headObserver.unobserve(e.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) headObserver.observe(sectionRef.current);
    return () => { cardObserver.disconnect(); headObserver.disconnect(); };
  }, []);

  const handleCardClick = (slug) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/tests/${slug}`);
  };

  const row1 = SERVICES.slice(0, 4);
  const row2 = SERVICES.slice(4, 7);

  return (
    <section className="ds-section" id="services">
      <Container fluid="lg">

        <div ref={sectionRef} className="ds-header text-center mb-5">
          <span className="ds-eyebrow">Our Diagnostic Services</span>
          <p className="ds-header__sub">
            We offer a wide range of diagnostic tests across multiple specialties.
            Click on any service to view the tests included.
          </p>
        </div>

        <Row className="g-3 justify-content-center mb-3">
          {row1.map((svc, i) => (
            <Col key={svc.id} xs={12} sm={6} lg={3} md={6}>
              <ServiceCard
                svc={svc}
                delay={i * 100}
                onClick={() => handleCardClick(svc.slug)}
                ref={(el) => (cardRefs.current[i] = el)}
              />
            </Col>
          ))}
        </Row>

        <Row className="g-3 justify-content-center">
          {row2.map((svc, i) => (
            <Col key={svc.id} xs={12} sm={6} lg={3} md={6}>
              <ServiceCard
                svc={svc}
                delay={(i + 4) * 100}
                onClick={() => handleCardClick(svc.slug)}
                ref={(el) => (cardRefs.current[i + 4] = el)}
              />
            </Col>
          ))}
        </Row>

      </Container>
    </section>
  );
}

const ServiceCard = forwardRef(function ServiceCard({ svc, delay, onClick }, ref) {
  return (
    <div
      className="ds-card"
      ref={ref}
      onClick={onClick}
      style={{ "--ds-delay": `${delay}ms`, "--ds-icon-color": svc.iconColor, "--ds-icon-bg": svc.iconBg }}
    >
      <div className="ds-card__header d-flex align-items-center gap-3">
        <div className="ds-card__icon-wrap flex-shrink-0">
          <i className={`${svc.icon} ds-card__icon`} />
        </div>
        <h3 className="ds-card__title mb-0">{svc.title}</h3>
      </div>

      <div className="ds-card__img-wrap">
        <img src={svc.image} alt={svc.title} loading="lazy" className="ds-card__img" />
      </div>

      <ul className="ds-card__list list-unstyled mb-0">
        {svc.tests.slice(0, 5).map((t, idx) => (
          <li key={idx} className="ds-card__list-item">
            <i className="fa-solid fa-check ds-card__check" />
            <span>{typeof t === "string" ? t : t.name}</span>
          </li>
        ))}
      </ul>

      <div className="ds-card__footer text-center">
        <button className="ds-card__btn">
          View Tests <i className="fa-solid fa-arrow-right ms-1" />
        </button>
      </div>
    </div>
  );
});